import React, { useState } from 'react';
import { LuLink, LuUnlink, LuCirclePlus, LuMenu, LuLoader } from 'react-icons/lu';
import Modal from '../components/Modal';
import AddBrokerModal from '../components/AddBrokerModal';
import apiClient from '../api/apiClient';

// --- Import Your Logo Images ---
import zerodhaLogo from '../assets/brokers/zerodha.png';
import dhanLogo from '../assets/brokers/dhan.jpeg';
import angeloneLogo from '../assets/brokers/angelone.png';
import upstoxLogo from '../assets/brokers/upstox.png';
import coinDCXLogo from '../assets/brokers/coinDCX.jpeg';

// --- The Main Component ---
const BrokerManagementPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [connectedBrokers, setConnectedBrokers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncingBrokers, setSyncingBrokers] = useState(new Set());
  const [disconnectingBrokers, setDisconnectingBrokers] = useState(new Set());

  const toggleDropdown = (id) => {
    setActiveDropdown(activeDropdown === id ? null : id);
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeDropdown && !event.target.closest('.dropdown-container')) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [activeDropdown]);

  const fetchBrokers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get('/brokers');
      const brokersWithLogos = response.data.map(broker => {
        let logo = null;
        if (broker.name.toLowerCase().includes('zerodha')) logo = zerodhaLogo;
        else if (broker.name.toLowerCase().includes('dhan')) logo = dhanLogo;
        else if (broker.name.toLowerCase().includes('angel one')) logo = angeloneLogo;
        else if (broker.name.toLowerCase().includes('upstox')) logo = upstoxLogo;
        else if (broker.name.toLowerCase().includes('coindcx')) logo = coinDCXLogo;
        return { ...broker, logo };
      });
      setConnectedBrokers(brokersWithLogos);
    } catch (error) {
      console.error('Error fetching brokers:', error);
      setError('Failed to load brokers. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchBrokers();
  }, []);

  const handleSync = async (brokerId, brokerName) => {
    setSyncingBrokers(prev => new Set(prev).add(brokerId));
    setActiveDropdown(null);

    try {
      await apiClient.post(`/brokers/${brokerId}/sync`);
      // Refresh brokers list to get updated sync time
      await fetchBrokers();
    } catch (error) {
      console.error('Error syncing broker:', error);
      alert(`Failed to sync ${brokerName}. Please try again.`);
    } finally {
      setSyncingBrokers(prev => {
        const newSet = new Set(prev);
        newSet.delete(brokerId);
        return newSet;
      });
    }
  };

  const handleDisconnect = async (brokerId, brokerName) => {
    if (!window.confirm(`Are you sure you want to disconnect ${brokerName}?`)) {
      return;
    }

    setDisconnectingBrokers(prev => new Set(prev).add(brokerId));
    setActiveDropdown(null);

    try {
      await apiClient.delete(`/brokers/${brokerId}`);
      // Refresh brokers list
      await fetchBrokers();
    } catch (error) {
      console.error('Error disconnecting broker:', error);
      alert(`Failed to disconnect ${brokerName}. Please try again.`);
    } finally {
      setDisconnectingBrokers(prev => {
        const newSet = new Set(prev);
        newSet.delete(brokerId);
        return newSet;
      });
    }
  };

  return (
    <div className="bg-slate-50 min-h-screen">
      <div className="container mx-auto max-w-4xl p-3 sm:p-4 md:p-6 space-y-6 sm:space-y-8 md:space-y-10">

        {/* === Page Header with CTA === */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-800">Broker Management</h1>
            <p className="text-sm sm:text-base text-slate-500 mt-1">Connect and manage your trading accounts.</p>
          </div>
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center space-x-2 bg-blue-600 text-white font-semibold px-3 sm:px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors transform active:scale-95 w-full sm:w-auto justify-center"
          >
            <LuCirclePlus size={18} />
            <span className="text-sm sm:text-base">Add New Broker</span>
          </button>
        </div>

        {loading && <p className="text-center text-gray-600">Loading brokers...</p>}
        {error && <p className="text-center text-red-600">{error}</p>}

        {!loading && !error && (
          <div className="space-y-3 sm:space-y-4">
            <h2 className="text-lg font-semibold text-slate-600 border-b pb-2">Connected Accounts</h2>
            <div className="space-y-3 sm:space-y-4">
              {connectedBrokers.length === 0 && <p className="text-center text-gray-500">No connected brokers found.</p>}
              {connectedBrokers.map(broker => (
                <div key={broker.id} className="bg-white p-3 sm:p-4 rounded-xl shadow-sm border border-transparent hover:border-blue-300 transition-all duration-300 transform hover:-translate-y-1">
                  {/* Mobile Layout */}
                  <div className="block md:hidden">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <img src={broker.logo} alt={`${broker.name} logo`} className="h-10 w-10 object-contain rounded-full border p-1" />
                        <div>
                          <p className="font-bold text-slate-800 text-sm sm:text-base">{broker.name}</p>
                          <span className="text-xs font-semibold text-green-800 bg-green-100 px-2 py-0.5 rounded-full">Active</span>
                        </div>
                      </div>
                      <div className="relative dropdown-container">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleDropdown(broker.id);
                          }}
                          className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
                        >
                          <LuMenu size={18} />
                        </button>
                        {activeDropdown === broker.id && (
                          <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-slate-200 z-10 min-w-[140px] dropdown-container">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSync(broker.id, broker.name);
                              }}
                              disabled={syncingBrokers.has(broker.id)}
                              className="flex items-center space-x-2 w-full px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {syncingBrokers.has(broker.id) ? (
                                <LuLoader className="animate-spin" size={14} />
                              ) : (
                                <LuLink size={14} />
                              )}
                              <span>{syncingBrokers.has(broker.id) ? 'Syncing...' : 'Sync'}</span>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDisconnect(broker.id, broker.name);
                              }}
                              disabled={disconnectingBrokers.has(broker.id)}
                              className="flex items-center space-x-2 w-full px-3 py-2 text-sm text-red-600 hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {disconnectingBrokers.has(broker.id) ? (
                                <LuLoader className="animate-spin" size={14} />
                              ) : (
                                <LuUnlink size={14} />
                              )}
                              <span>{disconnectingBrokers.has(broker.id) ? 'Disconnecting...' : 'Disconnect'}</span>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-xs text-slate-500">Balance</p>
                        <p className="font-semibold text-slate-700 truncate">{typeof broker.balance === 'string' ? broker.balance : broker.balance?.toString() || ''}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Margin</p>
                        <p className="font-semibold text-slate-700 truncate">{typeof broker.margin === 'string' ? broker.margin : broker.margin?.toString() || ''}</p>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-slate-100">
                      <p className="text-xs text-slate-500">Last sync: {typeof broker.sync === 'string' ? broker.sync : broker.sync?.toString() || ''}</p>
                    </div>
                  </div>

                  {/* Tablet & Desktop Layout */}
                  <div className="hidden md:flex items-center space-x-3 sm:space-x-4">
                    <img src={broker.logo} alt={`${broker.name} logo`} className="h-10 sm:h-12 w-10 sm:w-12 object-contain rounded-full border p-1 flex-shrink-0" />
                    <div className="flex-grow grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 items-center">
                      <div className="lg:col-span-1 min-w-0">
                        <p className="font-bold text-slate-800 text-sm sm:text-base truncate">{broker.name}</p>
                        <span className="text-xs font-semibold text-green-800 bg-green-100 px-2 py-0.5 rounded-full">Active</span>
                      </div>
                      <div className="text-left lg:text-center min-w-0">
                        <p className="text-xs sm:text-sm text-slate-500">Balance</p>
                        <p className="font-semibold text-slate-700 text-sm sm:text-base truncate">{typeof broker.balance === 'string' ? broker.balance : broker.balance?.toString() || ''}</p>
                      </div>
                      <div className="text-left lg:text-center min-w-0">
                        <p className="text-xs sm:text-sm text-slate-500">Margin</p>
                        <p className="font-semibold text-slate-700 text-sm sm:text-base truncate">{typeof broker.margin === 'string' ? broker.margin : broker.margin?.toString() || ''}</p>
                      </div>
                      <div className="flex items-center justify-end space-x-2 sm:space-x-4">
                        <button
                          className="flex items-center space-x-1 text-slate-500 hover:text-blue-600 font-medium transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                          title={`Last sync: ${typeof broker.sync === 'string' ? broker.sync : broker.sync?.toString() || ''}`}
                          onClick={() => handleSync(broker.id, broker.name)}
                          disabled={syncingBrokers.has(broker.id)}
                        >
                          {syncingBrokers.has(broker.id) ? (
                            <LuLoader className="animate-spin" size={14} />
                          ) : (
                            <LuLink size={14} className="sm:size-[16px]" />
                          )}
                          <span className="hidden sm:inline">{syncingBrokers.has(broker.id) ? 'Syncing...' : 'Sync'}</span>
                        </button>
                        <button
                          className="flex items-center space-x-1 text-slate-500 hover:text-red-600 font-medium transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                          onClick={() => handleDisconnect(broker.id, broker.name)}
                          disabled={disconnectingBrokers.has(broker.id)}
                        >
                          {disconnectingBrokers.has(broker.id) ? (
                            <LuLoader className="animate-spin" size={14} />
                          ) : (
                            <LuUnlink size={14} className="sm:size-[16px]" />
                          )}
                          <span className="hidden sm:inline">{disconnectingBrokers.has(broker.id) ? 'Disconnecting...' : 'Disconnect'}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Modal for Adding a Broker */}
        <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
          <AddBrokerModal onClose={() => setIsModalOpen(false)} onBrokerAdded={fetchBrokers} />
        </Modal>
      </div>
    </div>
  );
};

export default BrokerManagementPage;
